import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeedbackModal } from '@components';
import config from '@config';
import { ROUTE_PATHS } from '@constants';
import { ACCOUNT_STATUS, CAMPAIGN_STATUS, CONTENT_TYPE } from '@enums';
import { useInstall, useStore } from '@hooks';
import { Button, Icon } from '@ui';
import { Flex } from 'antd';
import { observer } from 'mobx-react';

const DISABLED_STATUSES = [
    CAMPAIGN_STATUS.DELETED,
    CAMPAIGN_STATUS.SUBMITTED,
    CAMPAIGN_STATUS.FROZEN,
    CAMPAIGN_STATUS.PRE_LAUNCHED
];

const TableItemActions = observer(({ campaignId }) => {
    const store = useStore();
    const { pauseCampaign, resumeCampaign, deleteCampaign, getCampaign } = store.campaignsStore;
    const campaign = getCampaign(campaignId);
    const { status, pending, isWaitingForProducts } = campaign;
    const { isActive, updateAccount, isPaused, isUninstalled, isShopify } = store.accountsStore;
    const { install, loading: isInstalling } = useInstall();
    const navigate = useNavigate();
    const { showFeedbackModal } = useFeedbackModal('campaign-paused');

    const disabled = DISABLED_STATUSES.includes(status) || pending;

    const handleCampaignClick = async () => {
        if (isInstalling || pending) {
            return;
        }

        if (isUninstalled && isShopify) {
            return install();
        }

        // "Waiting for products" campaigns cannot be set live from the dashboard. Clicking the action
        // converts them into a regular brand-initiated pause (backend sets pausedReason = BY_BRAND),
        // which also blocks the automatic resume once products return.
        if (isWaitingForProducts) {
            await pauseCampaign(campaignId);
            return;
        }

        if (isPaused) {
            await updateAccount({ status: ACCOUNT_STATUS.ACTIVE }, CONTENT_TYPE.JSON);
        }

        if (status === CAMPAIGN_STATUS.ACTIVE) {
            if (config.isFeedbackModalEnabled) {
                showFeedbackModal(() => pauseCampaign(campaignId));
            } else {
                await pauseCampaign(campaignId);
            }
        }

        if (status === CAMPAIGN_STATUS.PAUSED || status === CAMPAIGN_STATUS.CREATED) {
            resumeCampaign(campaignId);
        }
    };

    const buttonConfigs = [
        {
            onClick: handleCampaignClick,
            icon:
                status === CAMPAIGN_STATUS.ACTIVE || status === CAMPAIGN_STATUS.PRE_LAUNCHED || isWaitingForProducts ? (
                    <Icon.PauseIcon data-e2e={`campaigns_pause-${campaignId}-campaign_btn`} />
                ) : (
                    <Icon.PlayIcon data-e2e={`campaigns_resume-${campaignId}-campaign_btn`} />
                ),
            disabled
        },
        {
            onClick: () => navigate(`${ROUTE_PATHS.CAMPAIGNS}/edit/${campaignId}`),
            icon: <Icon.EditIcon data-e2e={`campaigns_edit-${campaignId}-campaign_btn`} />,
            disabled: !isActive || status === CAMPAIGN_STATUS.FROZEN || status === CAMPAIGN_STATUS.SUBMITTED
        },
        {
            onClick: () => deleteCampaign(campaignId),
            icon: <Icon.TrashIcon data-e2e={`campaigns_archive-${campaignId}-campaign_btn`} />,
            disabled: disabled
        }
    ];

    return (
        <Flex gap={4}>
            {buttonConfigs.map((config, i) => {
                return (
                    <Button
                        key={i}
                        fontSize='24px'
                        type='noStyle'
                        onClick={config.onClick}
                        padding='8px'
                        disabled={config.disabled}
                    >
                        {config.icon}
                    </Button>
                );
            })}
        </Flex>
    );
});

export default TableItemActions;
